import { MAX_CAMPAIGN_SCHEDULE_AHEAD_DAYS } from 'modules/campaigns/lib/constants';
import {
  getTakeoverCampaignId,
  getTakeoverCampaignMessageMedium,
  getTakeoverCampaignType,
  getTakeoverExperimentData,
  getTakeoverExperimentId,
  getTakeoverExperimentState,
  getTakeoverExperimentType,
} from 'modules/experiments/ducks/selectors';
import {
  SaveFormTrigger,
  TakeoverExperimentContextState,
  UpdateExperimentsArgs,
} from 'modules/experiments/types';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MODAL_VARIANTS } from 'ui/modal';
import { BaseOption } from 'ui/selects';
import { getNewDate, parseISOZonedDate } from 'utils/date';
import { useStateWithCallback } from 'utils/hooks/useStateWithCallback';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';
import {
  useCampaignSchedulerMsgParams,
  useDynamicSenderProjectSetting,
  useEmailSenders,
  useExperimentTakeoverVariantList,
  useExperimentsTranslations,
  useProjectsTimeZone,
  useUpdateExperimentingSettings,
} from '../hooks';
import { updateExperimentEditVariants } from '../takeover/ducks/actions';
import { getTakeoverExperimentLocale } from '../takeover/ducks/selectors';
import {
  abbreviateTimeZone,
  getBlastCampaignMetaInfo,
  getHoldoutMetaInfo,
  getSendTimeExperimentTimeZone,
  getTriggerCampaignMetaInfo,
  isBlastCampaignType,
  isBodyExperiment,
  isEmailMessageMedium,
  isEverythingExperiment,
  isFromNameAndSenderExperiment,
  isInAppMessageMedium,
  isRunningExperiment,
  isSTOExperiment,
  isSendTimeExperiment,
  isTriggerCampaignType,
} from '../utils';
import { useModalContext } from './ModalContext';
import { TakeoverExperimentContext } from './TakeoverExperimentContext';

const voidFn = () => {};

export const TakeoverExperimentContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const projectTimeZone = useProjectsTimeZone();
  const experimentData = useSelector(getTakeoverExperimentData);
  const type = useSelector(getTakeoverExperimentType);
  const campaignId = useSelector(getTakeoverCampaignId);
  const campaignType = useSelector(getTakeoverCampaignType);
  const experimentId = useSelector(getTakeoverExperimentId);
  const experimentState = useSelector(getTakeoverExperimentState);
  const currentLocale = useSelector(getTakeoverExperimentLocale);
  const fetchEmailSenders = useEmailSenders();
  const messageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const [emailSenders, setEmailSenders] = useState<BaseOption<string>[]>([]);
  const [useDynamicSenders, setUseDynamicSenders] = useState(false);
  const [unsavedVariantList, setUnsavedVariantList] = useState(false);
  const [unsavedSettings, setUnsavedSettings] = useState(false);
  const [isExperimentSaving, setIsExperimentSaving] = useState(false);
  const [isOverlayEnable, setIsOverlayEnable] = useState(false);
  const { showModal, hideModal } = useModalContext();

  const updateExperimentSettings = useUpdateExperimentingSettings({ experimentId });

  // Experiment form action triggers and callbacks
  const [resetDirtyVariantCard, setResetDirtyVariantCard] = useState(false);
  const [saveDirtyVariantCard, setSaveDirtyVariantCard] = useStateWithCallback<SaveFormTrigger>({
    trigger: false,
    onSuccess: voidFn,
    onError: voidFn,
  });
  const asyncSetSaveDirtyVariantCards = (newState: Partial<SaveFormTrigger>) => {
    return new Promise((resolve) =>
      setSaveDirtyVariantCard((prevState) => ({ ...prevState, ...newState }), resolve),
    );
  };
  const [saveDirtyExperimentSettings, setSaveDirtyExperimentSettings] =
    useStateWithCallback<SaveFormTrigger>({
      trigger: false,
      onSuccess: voidFn,
      onError: voidFn,
    });
  const asyncSetSaveDirtyExperimentSettings = (newState: Partial<SaveFormTrigger>) => {
    return new Promise((resolve) =>
      setSaveDirtyExperimentSettings((prevState) => ({ ...prevState, ...newState }), resolve),
    );
  };

  // Experiment form action dispatchers
  const dispatchSaveDirtyVariantCard = async (onSuccess = voidFn, onError = voidFn) => {
    /**
     * We first want to set the callback methods to context
     * Before we dispatch a save variant card signal
     */
    await asyncSetSaveDirtyVariantCards({
      onSuccess,
      onError,
    });

    setSaveDirtyVariantCard((prevState) => ({ ...prevState, trigger: !prevState.trigger }));
  };
  const dispatchSaveDirtyExperimentSettings = async (onSuccess = voidFn, onError = voidFn) => {
    /**
     * We first want to set the callback methods to context
     * Before we dispatch a save experiment settings signal
     */
    await asyncSetSaveDirtyExperimentSettings({
      onSuccess,
      onError,
    });

    setSaveDirtyExperimentSettings((prevState) => ({ ...prevState, trigger: !prevState.trigger }));
  };
  const dispatchResetDirtyVariantCard = () => setResetDirtyVariantCard((state) => !state);

  // Campaign type checks
  const isBlastCampaign = isBlastCampaignType(campaignType);
  const isTriggerCampaign = isTriggerCampaignType(campaignType);

  // Experiment type checks
  const isSTOExp = isSTOExperiment(type);
  const isSendTimeExp = isSendTimeExperiment(type);
  const isSendersExp = isFromNameAndSenderExperiment(type);
  const isBodyExp = isBodyExperiment(type);
  const isEverythingExp = isEverythingExperiment(type);

  // Experiment medium checks
  const isEmailMedium = isEmailMessageMedium(messageMedium);
  const isInAppMedium = isInAppMessageMedium(messageMedium);

  // Experiment type and medium checks
  const isEmailEverything = isEmailMedium && isEverythingExp;
  const isEmailBodyExperiment = isEmailMedium && isBodyExp;
  const isEmailSendersExperiment = isEmailMedium && isSendersExp;
  const isInAppEverythingExperiment = isInAppMedium && isEverythingExp;

  // Send time experiment meta info
  const {
    sendTimeStartTimeZone: defaultStartTimeZone,
    sendTimeUseRecipientTimeZones: defaultUseRecipientTimeZones,
  } = getSendTimeExperimentTimeZone(experimentData, projectTimeZone);
  const [sendTimeStartTimeZone, setSendTimeStartTimeZone] = useState(defaultStartTimeZone);
  const [sendTimeUseRecipientTimeZones, setSendTimeUseRecipientTimeZones] = useState(
    defaultUseRecipientTimeZones,
  );
  const scheduleStartDate = getNewDate({ isNow: true, timeZone: sendTimeStartTimeZone }).startOf(
    'day',
  );

  /**
   * Currently, we don't allow users to edit
   * the template content of these experiments
   * inside the experiment editor
   */
  const isVariantNotEditable = isEmailBodyExperiment || isInAppEverythingExperiment || isSTOExp;

  // Fetch the email from name and sender's list for the following experiment
  const shouldFetchFromNameAndSendersList = isEmailSendersExperiment || isEmailEverything;
  const fetchDynamicSenderProjectSetting = useDynamicSenderProjectSetting(
    shouldFetchFromNameAndSendersList,
  );
  const dynamicSenderProjectSetting = fetchDynamicSenderProjectSetting();

  /**
   * Fetch the sender's email list and the project settings on load
   * for experiments that should fetch these meta info
   * */
  useEffect(() => {
    if (messageMedium && shouldFetchFromNameAndSendersList) {
      fetchEmailSenders(messageMedium).then((data) => setEmailSenders(data));
    }
  }, [messageMedium, shouldFetchFromNameAndSendersList]);

  useEffect(() => {
    if (dynamicSenderProjectSetting) setUseDynamicSenders(dynamicSenderProjectSetting);
  }, [dynamicSenderProjectSetting]);

  const saveExperiment = async (updateExperimentSettingsArgs: UpdateExperimentsArgs) => {
    setIsExperimentSaving(true);
    const response = await updateExperimentSettings(updateExperimentSettingsArgs, currentLocale);
    setIsExperimentSaving(false);
    return response;
  };

  // Show the unsaved prompt if the user tries to exit page with unsaved changes
  useUnsavedChangesPrompt(unsavedVariantList || unsavedSettings);

  // Method to open the unsaved variant modal
  const unsavedVariantModal = (cardAction?: () => void) =>
    showModal({
      variant: MODAL_VARIANTS.WARNING,
      id: 'unsaved-variants-modal',
      title: t('variantCard.unsavedModal.header'),
      body: t('variantCard.unsavedModal.body'),
      buttonText: t('variantCard.unsavedModal.button'),
      isDestructive: false,
      secondaryButton: {
        buttonText: t('variantCard.unsavedModal.secondaryButton'),
        onConfirm: () => {
          /**
           * The user has selected to leave without saving.
           * So, first we want to reset all the dirty variant cards
           * In our case it will be the currently selected card
           * And, then simply proceed with the default card action
           * Finally close the dialog
           */
          dispatchResetDirtyVariantCard();
          if (cardAction) {
            cardAction();
          }
          hideModal();
        },
      },
      onConfirm: () => {
        /**
         * We first save all variant cards with dirty form state
         * In our case it will be the currently selected card
         * And, then we simply proceed with the default card action
         * If the save was successful
         */
        dispatchSaveDirtyVariantCard(cardAction ? () => cardAction() : voidFn);
        hideModal();
      },
    });

  // Fetch Campaign scheduler message params
  const fetchCampaignSchedulerMsgParams = useCampaignSchedulerMsgParams(
    campaignId,
    isBlastCampaign,
  );

  // Methods to re-fetch different sections of the experiment takeover
  const reFetchVariantList = useExperimentTakeoverVariantList(experimentId, currentLocale);

  const context: TakeoverExperimentContextState = {
    isBlastCampaign,
    isTriggerCampaign,
    isRunningExperiment: isRunningExperiment(campaignType, experimentState),
    isInAppMessageMedium: isInAppMedium,
    isSendTimeExperiment: isSendTimeExp,
    isSTOExperiment: isSTOExp,
    isVariantNotEditable,
    isExperimentSaving,
    campaignSchedulerMsgParams: {
      scheduleType: fetchCampaignSchedulerMsgParams(),
    },
    fromNameAndSenderMetaInfo: {
      useDynamicSenders,
      emailSenders,
    },
    holdoutMetaInfo: getHoldoutMetaInfo(experimentData),
    triggerCampaignMetaInfo: getTriggerCampaignMetaInfo(experimentData),
    blastCampaignMetaInfo: getBlastCampaignMetaInfo(experimentData),
    sendTimeMetaInfo: {
      setSendTimeStartTimeZone,
      setSendTimeUseRecipientTimeZones,
      sendTimeStartTimeZone,
      sendTimeUseRecipientTimeZones,
      sendTimeStartTimeZoneAbbr: abbreviateTimeZone(sendTimeStartTimeZone),
      sendTimeOfAllVariants: experimentData.variations?.map(({ id, sendTime }) => ({
        id,
        sendTime: sendTime
          ? parseISOZonedDate(sendTime).second(0).millisecond(0).toISOString()
          : null,
      })),
      controlStartTime: experimentData.variations?.[0]?.sendTime ?? '',
      projectTimeZone,
      scheduleDateRange: {
        start: scheduleStartDate,
        end: scheduleStartDate.clone().add(MAX_CAMPAIGN_SCHEDULE_AHEAD_DAYS, 'days'),
      },
    },
    overlay: {
      isOverlayEnable,
      setIsOverlayEnable,
    },
    unsavedChanges: {
      setUnsavedVariantList,
      setUnsavedSettings,
      unsavedVariantModal,
      variantList: unsavedVariantList,
      settings: unsavedSettings,
    },
    reFetch: {
      variantList: async () => {
        const variantList = await reFetchVariantList();
        dispatch(updateExperimentEditVariants(variantList));
      },
    },
    formActions: {
      saveDirtyVariantCard,
      saveDirtyExperimentSettings,
      resetDirtyVariantCard,
      dispatchSaveDirtyVariantCard,
      dispatchSaveDirtyExperimentSettings,
      dispatchResetDirtyVariantCard,
      asyncDispatchSaveDirtyVariantCard: () =>
        new Promise((resolve, reject) => {
          dispatchSaveDirtyVariantCard(() => resolve(''), reject);
        }),
      asyncDispatchSaveDirtyExperimentSettings: () =>
        new Promise((resolve, reject) => {
          dispatchSaveDirtyExperimentSettings(() => resolve(''), reject);
        }),
    },
    saveExperiment,
  };

  return (
    <TakeoverExperimentContext.Provider value={context}>
      {children}
    </TakeoverExperimentContext.Provider>
  );
};
